import React, { useState } from "react";
import ErrorMessage from "../../Components/ErrorMessage";
import Spinner from "../../Components/Spinner";
import MandatorySign from "../../Components/MandatorySign";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import FormatDateTime from "../../Functions/FormatDate";
import PaymentPlanLoader from "../../Loaders/PaymentPlanLoader";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;


const PaymentMethodStep = ({
    account_id,
    handleStep,
    soundClick,
    error,
    subscription,
    selectedPackage,
    loadingSelectedPackage,
    setError
}) => {
    const { t } = useLanguage()
    const navigate = useNavigate()
    const stripe = useStripe();
    const elements = useElements();
    const [paymentName, setPaymentName] = useState("")
    const [loadingPayment, setLoadingPayment] = useState(false)

    const computedColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--originalBlack")
        .trim();

    const computedColorPlaceholder = getComputedStyle(document.documentElement)
        .getPropertyValue("--originalGray")
        .trim();

    const getNextBillingDate = (interval) => {
        const date = new Date();

        if (interval === "monthly") {
            date.setMonth(date.getMonth() + 1);
        } else {
            date.setFullYear(date.getFullYear() + 1);
        }

        return date;
    };

    const formattedDate = FormatDateTime(getNextBillingDate(selectedPackage.interval), "date_only");

    const handlePayment = async () => {
        setError({ message: "", display: false });
        setLoadingPayment(true);

        if (!paymentName) {
            setError({ message: "Payment method label is required...", display: true });
            setLoadingPayment(false);
            return;
        }

        const cardElement = elements.getElement(CardElement);

        const { paymentMethod, error } = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
        });

        if (error) {
            setError({ message: "Card details are required...", display: true });
            setLoadingPayment(false);
            return
        }

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/payment_onboarding`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id,
                    payment_name: paymentName,
                    payment_method_id: paymentMethod.id,
                    selected_plan: selectedPackage
                }),
            });

            if (!response.ok) throw new Error("Failed to fetch user session");

            const data = await response.json();
            const status = data.status
            if (status == "OK") {
                navigate("/onboarding/done")
            }

            setTimeout(() => setLoadingPayment(false), 3000);
        } catch (error) {
            console.error("Error fetching session:", error);
            setLoadingPayment(false)
            setError({ message: "An error occurred while paying... retry in a few minutes", display: true })
        }
    };

    return (
        <div className="steps_main_container">
            <div className="welcome_step_cont">
                <div className="welcome_step_cont_inner">
                    <p className="step_title fade-in">{t.onboarding.payment.title}</p>
                    <p className="step_description fade-in" dangerouslySetInnerHTML={{ __html: t.onboarding.payment.subtitle }}></p>
                    <div className="payment_method_step_cont">
                        {loadingSelectedPackage ? (
                            <PaymentPlanLoader />
                        ) : (
                            <>
                                <div className="payment_method_step_cont_inner fade-in">
                                    <div className="payment_method_step_cont_inner_header">
                                        <p className="payment_method_step_cont_inner_title">{t.onboarding.payment.checkout_for} "{selectedPackage.name}"</p>
                                        <p className="payment_method_step_cont_inner_description">{selectedPackage.description}</p>
                                    </div>

                                    <div className="footer_checkout">
                                        <p className="footer_checkout_title">{t.onboarding.payment.summary}</p>
                                        <div className="footer_checkout_footer">
                                            <p>{t.onboarding.payment.ammount}</p>
                                            <p className="footer_checkout_ammount">${Number(selectedPackage.price).toFixed(2)}</p>
                                        </div>
                                        <div className="footer_checkout_footer">
                                            <p>{t.onboarding.payment.next_payment} <MandatorySign message={t.onboarding.payment.date_info} icon="ingo" size="12px" /></p>
                                            <p>{formattedDate}</p>
                                        </div>
                                        <div className="footer_checkout_footer">
                                            <p>{t.onboarding.payment.interval}</p>
                                            <p>
                                                {selectedPackage.interval.charAt(0).toUpperCase() + selectedPackage.interval.slice(1)}
                                            </p>
                                        </div>
                                        <div className="footer_checkout_footer">
                                            <p>{t.onboarding.payment.recurring}</p>
                                            <p>{t.yes}</p>
                                        </div>
                                    </div>

                                </div>
                                <div className="payment_method_step_cont_inner fade-in">
                                    <div className="new_card_modal">
                                        <div className="modal_profile_input_cont">
                                            <div className="label_input">
                                                <label className="input_label">{t.payments.label} <MandatorySign message={t.payments.label_required} /></label>
                                                <input
                                                    type="text"
                                                    name="paymentMethodName"
                                                    value={paymentName}
                                                    onChange={(e) => setPaymentName(e.target.value)}
                                                    placeholder="Ex. My card"
                                                    className="register_form_first fade-in"
                                                    autoComplete="off"
                                                />
                                            </div>

                                            <div className="label_input">
                                                <label className="input_label">
                                                    {t.payments.card_details} <MandatorySign message={t.payments.card_details_required} />
                                                </label>

                                                <div className="card_det_container">
                                                    <div className="card_det">
                                                        <CardElement
                                                            options={{
                                                                hidePostalCode: true,
                                                                style: {
                                                                    base: {
                                                                        fontSize: "14px",
                                                                        color: computedColor,
                                                                        "::placeholder": {
                                                                            color: computedColorPlaceholder
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                            </div>

                                        </div>
                                        <p className="role_warning_text" dangerouslySetInnerHTML={{ __html: t.payments.sensitive_mex }}></p>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="steps_btn_cont">
                <button
                    className="steps_btn"
                    onClick={() => handleStep("back", "subscription")}
                >
                    <i className="fa-solid fa-chevron-left"></i> {t.onboarding.back}
                </button>
                <div>
                    <ErrorMessage err={error} />
                </div>
                <button className="steps_btn fade-in" disabled={loadingSelectedPackage} onClick={handlePayment}>
                    {loadingPayment ? (
                        <Spinner loading={true} />
                    ) : (
                        <>{t.payments.pay_continue} <i className="fa-solid fa-chevron-right"></i></>
                    )}
                </button>
            </div>
        </div>
    )
}

export default PaymentMethodStep